<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div style="max-width:760px">
      <h3 class="mb-1">Bring Config</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        Bring (Posten) frakt for POS-butikken: rater i checkout + auto-booking av frakt-etikett etter betaling.
        Lagres i Couchbase (ikke i kildekode/git); API-nøkkelen lagres <strong>kryptert</strong>.
        Fail-soft: ukonfigurert → flat fallback-frakt, booking hoppes over.
      </div>

      <div class="alert alert-info mt-3 py-2" style="font-size:0.88rem">
        <font-awesome-icon :icon="['fas', 'lock']" />
        API-nøkkelen vises aldri her igjen etter lagring. La feltet stå <strong>tomt</strong> for å beholde
        eksisterende — fyll det kun ut for å erstatte.
      </div>

      <config-lock ref="lock" :configured="isConfigured">
      <h5 class="mt-4 mb-1">Frakt-rater (Shipping Guide)</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label mb-1 bring-label">Mybring bruker-e-post (API uid)</label>
          <input class="form-control" type="text" v-model="form.apiUid" placeholder="ops@cagista.com" autocomplete="off" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 bring-label">API-nøkkel</label>
          <div class="mb-1">
            <span class="badge" :class="config.apiKeySet ? 'bg-success' : 'bg-secondary'">
              {{ config.apiKeySet ? 'Konfigurert ✓' : 'Ikke satt — fallback-frakt brukes' }}
            </span>
          </div>
          <div style="position:relative">
            <input :type="showKey ? 'text' : 'password'" class="form-control" style="padding-right:38px"
                   v-model="form.apiKey" autocomplete="new-password"
                   :placeholder="config.apiKeySet ? '•••• (lagret — la stå tomt)' : 'mybring.com → Settings → API'" />
            <button type="button" @click="showKey = !showKey" :title="showKey ? 'Skjul' : 'Vis'"
                    style="position:absolute; right:10px; top:50%; transform:translateY(-50%); border:none; background:none; color:var(--bo-muted); cursor:pointer; padding:0; line-height:1;">
              <font-awesome-icon :icon="['fas', showKey ? 'eye-slash' : 'eye']" />
            </button>
          </div>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Fra-postnummer (lager)</label>
          <input class="form-control" type="text" v-model="form.fromPostalCode" placeholder="0150" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Kundenummer</label>
          <input class="form-control" type="text" v-model="form.customerNumber" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Valgfritt for fraktpriser (gir avtalte nettopriser), men <strong>påkrevd for å lage
            frakt-etiketter</strong>. Bruk hovedkundenummeret — det er det Mybring ber om for innenlands
            forsendelser, ikke PARCELS_NORWAY-varianten.
          </span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Produktkode</label>
          <input class="form-control" type="text" v-model="form.productCode" placeholder="5000" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            <strong>5000 = Bedriftspakke</strong> — kjøres til bedriftsadressen i åpningstiden, som er det
            vi tilbyr. 5800 er «pakke til hentested»: mottakeren får SMS og henter selv i Post-i-butikk.
          </span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Fallback-frakt (øre, eks. mva)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.fallbackRateMinor" />
          <span style="color:var(--bo-muted); font-size:0.78rem">29900 = kr 299 <strong>eks. mva</strong> — beløpet som vises på Frakt-linjen. Kjøperen betaler kr 373,75 inkl. mva. Brukes når Bring er utilgjengelig/ukonfigurert.</span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Dropship-frakt (øre, eks. mva)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.dropshipRateMinor" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            19900 = kr 199 <strong>eks. mva</strong> (kr 248,75 inkl. mva). Legges på én gang når ordren inneholder varer leverandøren sender direkte —
            vi betaler frakten deres. Kommer i tillegg til Bring-frakten for det vi selv pakker.
          </span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Standardvekt (gram)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.defaultPackageWeightGrams" />
          <span style="color:var(--bo-muted); font-size:0.78rem">Brukes når produktene mangler vekt.</span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Kolli (L × B × H cm)</label>
          <div class="d-flex gap-1">
            <input class="form-control" type="number" min="1" v-model.number="form.packageLengthCm" />
            <input class="form-control" type="number" min="1" v-model.number="form.packageWidthCm" />
            <input class="form-control" type="number" min="1" v-model.number="form.packageHeightCm" />
          </div>
        </div>
        <div class="col-12">
          <!-- The project's own switch: backoffice.css sets `.form-switch input { display:none }`
               and draws the toggle from the <i>, so Bootstrap's form-check markup renders nothing. -->
          <div class="form-switch-wrapper" style="clear:both; overflow:auto; float:none">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="form.weightInKg" />
                <i></i>
                <span>Send vekt i kg til rate-API-et
              <span style="color:var(--bo-muted); font-size:0.82rem">— la stå <strong>av</strong>: Bring vil ha gram. Bekreftet mot produksjon 04.09.2026.</span></span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <h5 class="mt-4 mb-1">Booking (frakt-etikett)</h5>
      <div class="row g-3 mt-0">
        <div class="col-12">
          <!-- The project's own switch: backoffice.css sets `.form-switch input { display:none }`
               and draws the toggle from the <i>, so Bootstrap's form-check markup renders nothing. -->
          <div class="form-switch-wrapper" style="clear:both; overflow:auto; float:none">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="form.bookingEnabled" />
                <i></i>
                <span>Auto-book frakt ved betalt ordre</span>
              </div>
            </label>
            <!-- Stated here because this is where someone flips it, and the consequence is invisible
                 afterwards: nothing on the order page says a label was never asked for. -->
            <span style="color:var(--bo-muted); font-size:0.78rem; display:block; margin-top:0.35rem">
              <strong>Nødbrems, ikke en daglig innstilling.</strong> Fraktprisene virker som før — bare
              bookingen faller bort, så hver betalt ordre må bookes for hånd i Mybring. Bryteren er
              <strong>global</strong>, ikke per ordre: skal du stoppe én sending, la den stå på og
              kanseller den ene i Mybring i stedet.
            </span>
          </div>
          <!-- The project's own switch: backoffice.css sets `.form-switch input { display:none }`
               and draws the toggle from the <i>, so Bootstrap's form-check markup renders nothing. -->
          <div class="form-switch-wrapper" style="clear:both; overflow:auto; float:none">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="form.test" />
                <i></i>
                <span>Testmiljø
              <span style="color:var(--bo-muted); font-size:0.82rem">— book mot Bring test først; slå av for produksjon.</span></span>
              </div>
            </label>
          </div>
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 bring-label">Avsender (firma/lager)</label>
          <input class="form-control" type="text" v-model="form.senderName" placeholder="CAGISTA AS" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 bring-label">Adresse</label>
          <input class="form-control" type="text" v-model="form.senderAddressLine" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Postnummer</label>
          <input class="form-control" type="text" v-model="form.senderPostalCode" placeholder="tom = fra-postnummer" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Sted</label>
          <input class="form-control" type="text" v-model="form.senderCity" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Landkode</label>
          <input class="form-control" type="text" v-model="form.senderCountryCode" placeholder="NO" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Kontaktperson</label>
          <input class="form-control" type="text" v-model="form.senderContactName" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">Telefon</label>
          <input class="form-control" type="text" v-model="form.senderPhone" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 bring-label">E-post</label>
          <input class="form-control" type="text" v-model="form.senderEmail" />
        </div>
      </div>

      <div class="mt-4 d-flex align-items-center gap-3">
        <button class="bo_btn" @click="save" :disabled="saving">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
        <span v-if="message" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
      </div>
      </config-lock>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import SettingsService from "@/services/settings/SettingsService";

export default {
  name: "BringConfig",
  components: { LoadingSpinner, ConfigLock },
  computed: {
    // Already configured (→ starts locked): Bring has a stored API key (or the Mybring API uid is set).
    isConfigured() { return !!(this.config.apiKeySet || this.config.apiUid); }
  },
  data() {
    return {
      loading: true,
      config: {},
      form: {
        apiUid: "", apiKey: "", clientUrl: "", customerNumber: "", fromPostalCode: "",
        productCode: "5000", fallbackRateMinor: 29900, dropshipRateMinor: 19900, defaultPackageWeightGrams: 2000,
        weightInKg: false, packageLengthCm: 30, packageWidthCm: 25, packageHeightCm: 15,
        bookingEnabled: true, test: false,
        senderName: "", senderAddressLine: "", senderCity: "", senderPostalCode: "",
        senderCountryCode: "NO", senderContactName: "", senderPhone: "", senderEmail: ""
      },
      showKey: false,
      saving: false,
      message: "",
      error: false
    };
  },
  mounted() {
    this.loadConfig();
  },
  methods: {
    loadConfig() {
      SettingsService.getBringConfig().then(
        (response) => {
          this.config = response.data || {};
          Object.keys(this.form).forEach((k) => {
            if (k !== "apiKey" && this.config[k] !== undefined && this.config[k] !== null) {
              this.form[k] = this.config[k];
            }
          });
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke hente konfigurasjon.");
        }
      );
    },
    save() {
      this.saving = true;
      this.message = "";
      this.error = false;
      SettingsService.saveBringConfig(this.form).then(
        (response) => {
          this.saving = false;
          this.config = response.data || {};
          this.form.apiKey = "";   // don't keep the secret in the DOM
          this.showKey = false;
          this.error = false;
          this.message = "Lagret ✓ — tas i bruk umiddelbart (ingen restart).";
          this.$refs.lock.relock();   // re-lock after a successful save
        },
        (error) => {
          this.saving = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke lagre konfigurasjon.");
        }
      );
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
.bring-label {
  font-size: 0.85rem;
  color: var(--bo-muted);
}
</style>
